// B4 Vacantes y campañas: alta de vacante con los criterios en tres listas (descarta / marca / cumple)
// que el cliente firma, anuncios ligados y números por anuncio.
import { ArrowLeft, CheckCircle2, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { useCreate, useGetOne, useNotify, useRefresh, useUpdate } from "ra-core";
import { Link, useParams } from "react-router";
import { ArrayInput } from "@/components/admin/array-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dinero, fechaCorta } from "@/reclutamiento/logica";
import type { Anuncio, Candidato, CriterioVacante, EstadoVacante, Vacante } from "@/reclutamiento/tipos";
import { Encabezado, MarcaSemaforo, Pildora, Seccion } from "../comun";
import { useAnuncios, useCandidatos, useClientes, useVacantes } from "../datos";

const ETQ_ESTADO: Record<EstadoVacante, string> = { borrador: "Borrador", activa: "Activa", pausada: "Pausada", cerrada: "Cerrada" };
const tonoEstado = (e: EstadoVacante) => (e === "activa" ? "ok" : e === "borrador" ? "revisar" : "neutro") as "ok" | "revisar" | "neutro";

const cuenta = (ks: Candidato[]) => ({
  ok: ks.filter((k) => k.veredicto === "cumple").length,
  yel: ks.filter((k) => k.veredicto === "revisar").length,
  red: ks.filter((k) => k.veredicto === "descartado").length,
});

const Semaforos = ({ ks }: { ks: Candidato[] }) => {
  const c = cuenta(ks);
  return (
    <span className="num inline-flex items-center gap-2 text-sm">
      <span className="inline-flex items-center gap-1"><MarcaSemaforo tipo="cumple" size={14} />{c.ok}</span>
      <span className="inline-flex items-center gap-1"><MarcaSemaforo tipo="revisar" size={14} />{c.yel}</span>
      <span className="inline-flex items-center gap-1"><MarcaSemaforo tipo="descartado" size={14} />{c.red}</span>
    </span>
  );
};

/** Números por anuncio: mensajes, candidatos, 🟢🟡🔴 y costo por candidato que cumple. */
const TablaAnuncios = ({ anuncios, candidatos }: { anuncios: Anuncio[]; candidatos: Candidato[] }) => {
  if (!anuncios.length) return <p className="text-sm text-muted-foreground">Sin anuncios ligados.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr><th className="py-1 pr-3">Anuncio</th><th className="pr-3">Mensajes</th><th className="pr-3">Candidatos</th><th className="pr-3">Semáforo</th><th className="pr-3">Gasto</th><th>Costo por 🟢</th></tr>
        </thead>
        <tbody>
          {anuncios.map((a) => {
            const ks = candidatos.filter((k) => k.anuncio === a.codigo);
            const ok = cuenta(ks).ok;
            return (
              <tr key={a.id} className="border-t">
                <td className="py-2 pr-3"><Pildora tono="acento">{a.codigo}</Pildora> <span className="text-xs text-muted-foreground">{a.tema}</span></td>
                <td className="num pr-3">{a.mensajes}</td>
                <td className="num pr-3">{ks.length}</td>
                <td className="pr-3"><Semaforos ks={ks} /></td>
                <td className="num pr-3">{dinero(a.gasto)}</td>
                <td className="num">{ok ? dinero(a.gasto / ok) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const VacantesLista = () => {
  const { data: vacantes, isPending } = useVacantes();
  const { data: clientes } = useClientes();
  const { data: candidatos } = useCandidatos();
  const { data: anuncios } = useAnuncios();
  const [cliente, setCliente] = useState("");
  const lista = vacantes.filter((v) => !cliente || v.cliente_id === cliente);
  return (
    <div>
      <Encabezado
        ceja="B4"
        titulo="Vacantes y campañas"
        sub="Cada vacante lleva sus criterios en tres listas, que el cliente firma, y sus anuncios con sus números."
        acciones={<Button asChild size="sm"><Link to="/vacantes/create"><Plus className="size-4" /> Alta de vacante</Link></Button>}
      />
      <select className="mb-3 h-9 rounded-md border bg-background px-2 text-sm" value={cliente} onChange={(e) => setCliente(e.target.value)} aria-label="Cliente">
        <option value="">Todos los clientes</option>
        {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
      </select>
      {isPending ? <p className="text-sm text-muted-foreground">Cargando…</p> : null}
      <div className="grid gap-3 lg:grid-cols-2">
        {lista.map((v) => {
          const ks = candidatos.filter((k) => k.vacante_id === v.id);
          const ads = anuncios.filter((a) => a.vacante_id === v.id);
          return (
            <Link key={v.id} to={`/vacantes/${v.id}/show`} className="block rounded-xl border bg-card p-4 transition hover:shadow-md">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{clientes.find((c) => c.id === v.cliente_id)?.nombre}</p>
                  <p className="font-semibold">{v.puesto}</p>
                  <p className="text-xs text-muted-foreground">{v.zona} · {v.criterios.length} criterios · {v.firmada_en ? `firmada el ${fechaCorta(v.firmada_en)}` : "sin firmar"}</p>
                </div>
                <Pildora tono={tonoEstado(v.estado)}>{ETQ_ESTADO[v.estado]}</Pildora>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <Semaforos ks={ks} />
                <span className="text-xs text-muted-foreground">{ads.map((a) => a.codigo).join(", ") || "sin anuncios"} · {ks.length} candidatos</span>
              </div>
              {v.estado === "activa" && ks.length === 0 ? <p className="mt-2 text-xs text-[var(--bad-ink)]">Vacante activa sin candidatos</p> : null}
            </Link>
          );
        })}
      </div>
      <Seccion titulo="Números por anuncio (todas las vacantes)" className="mt-4">
        <TablaAnuncios anuncios={anuncios.filter((a) => a.tipo === "vacante" && (!cliente || lista.some((v) => v.id === a.vacante_id)))} candidatos={candidatos} />
      </Seccion>
    </div>
  );
};

const TresListas = ({ criterios }: { criterios: CriterioVacante[] }) => {
  const grupo = (efecto: CriterioVacante["efecto"]) => criterios.filter((c) => c.efecto === efecto);
  const col = (titulo: string, tipo: "descartado" | "revisar" | "cumple", items: CriterioVacante[], texto: (c: CriterioVacante) => string) => (
    <div className="rounded-lg border p-3">
      <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><MarcaSemaforo tipo={tipo} size={16} /> {titulo}</p>
      {items.length ? (
        <ul className="space-y-1.5 text-sm">{items.map((c) => <li key={c.clave}>{texto(c)}{c.pregunta ? <span className="block text-xs text-muted-foreground">Pregunta: {c.pregunta}</span> : null}</li>)}</ul>
      ) : <p className="text-xs text-muted-foreground">Nada en esta lista.</p>}
    </div>
  );
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {col("Descarta", "descartado", grupo("descarta"), (c) => c.texto_no || `No: ${c.texto}`)}
      {col("Marca para revisar", "revisar", grupo("marca"), (c) => c.texto_no || `Por confirmar: ${c.texto}`)}
      {col("Cuenta como cumple", "cumple", criterios, (c) => c.texto)}
    </div>
  );
};

export const VacanteFicha = () => {
  const { id } = useParams();
  const { data: v, isPending, error, refetch } = useGetOne<Vacante & { id: string }>("vacantes", { id: id! });
  const { data: clientes } = useClientes();
  const { data: candidatos } = useCandidatos();
  const { data: anuncios } = useAnuncios();
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [nuevo, setNuevo] = useState({ codigo: "", tema: "", gasto: "" });
  // El motivo y «Reintentar» los pone el aviso de arriba (ErroresDeConsulta); aquí no se dice «no existe».
  if (error) return <p className="text-sm text-muted-foreground">No se pudo abrir la ficha. <button type="button" className="underline" onClick={() => refetch()}>Reintentar</button></p>;
  if (isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (!v) return <p>No existe esa vacante.</p>;
  const cli = clientes.find((c) => c.id === v.cliente_id);
  const ks = candidatos.filter((k) => k.vacante_id === v.id);
  const ads = anuncios.filter((a) => a.vacante_id === v.id);
  const activar = () =>
    update("vacantes", { id: v.id, data: { estado: "activa", activa: true, firmada_en: v.firmada_en ?? new Date().toISOString().slice(0, 10) }, previousData: v }, {
      onSuccess: () => { notify("Vacante firmada y activa: el bot ya puede entrevistar para ella", { type: "info" }); refresh(); },
    });
  const ligar = () => {
    if (!nuevo.codigo.trim()) return;
    create("anuncios", { data: { codigo: nuevo.codigo.trim().toUpperCase(), tema: nuevo.tema.trim() || v.puesto, tipo: "vacante", vacante_id: v.id, gasto: Number(nuevo.gasto) || 0, mensajes: 0, activo: true } }, {
      onSuccess: () => { setNuevo({ codigo: "", tema: "", gasto: "" }); notify("Anuncio ligado", { type: "info" }); refresh(); },
    });
  };
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-3 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm"><Link to="/vacantes"><ArrowLeft className="size-4" /> Vacantes</Link></Button>
        <div className="flex gap-2">
          {v.estado !== "activa" ? <Button size="sm" onClick={activar}><CheckCircle2 className="size-4" /> Firmada: activar</Button> : null}
          <Button asChild variant="outline" size="sm"><Link to={`/vacantes/${v.id}`}><Pencil className="size-4" /> Editar</Link></Button>
        </div>
      </div>
      <Encabezado
        ceja={cli?.nombre}
        titulo={v.puesto}
        sub={<>{v.zona}{v.horario ? ` · ${v.horario}` : ""}{v.sueldo ? ` · ${v.sueldo}${v.sueldo_autorizado ? "" : " (el cliente no autorizó mostrarlo)"}` : ""}</>}
        acciones={<Pildora tono={tonoEstado(v.estado)}>{ETQ_ESTADO[v.estado]}{v.firmada_en ? ` · firmada ${fechaCorta(v.firmada_en)}` : ""}</Pildora>}
      />
      {v.estado === "borrador" ? (
        <p className="mb-4 rounded-lg border border-dashed p-3 text-sm">
          Borrador creado desde el prospecto. Falta confirmar los criterios con el cliente (qué descarta y qué solo marca), que los firme, y activarla.
        </p>
      ) : null}
      <Seccion titulo="Criterios que el cliente firma" className="mb-4">
        <TresListas criterios={v.criterios} />
      </Seccion>
      <Seccion titulo="Así se clasifica" className="mb-4">
        <ul className="space-y-1 text-sm">
          <li className="flex items-center gap-2"><MarcaSemaforo tipo="descartado" size={15} /> Si no cumple un criterio que <b>descarta</b> → Descartado, con ese motivo.</li>
          <li className="flex items-center gap-2"><MarcaSemaforo tipo="revisar" size={15} /> Si le falta un dato o no cumple uno que <b>marca</b> → Revisar, con el motivo.</li>
          <li className="flex items-center gap-2"><MarcaSemaforo tipo="cumple" size={15} /> Si cumple todo → Cumple.</li>
        </ul>
        <div className="mt-3"><Semaforos ks={ks} /> <Link className="ml-2 text-sm underline" to="/candidatos">Ver candidatos</Link></div>
      </Seccion>
      <Seccion titulo="Anuncios ligados">
        <TablaAnuncios anuncios={ads} candidatos={candidatos} />
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
          <input className="h-9 w-24 rounded-md border bg-background px-2 text-sm" placeholder="Código" value={nuevo.codigo} onChange={(e) => setNuevo({ ...nuevo, codigo: e.target.value })} aria-label="Código del anuncio" />
          <input className="h-9 w-64 rounded-md border bg-background px-2 text-sm" placeholder="Tema del anuncio" value={nuevo.tema} onChange={(e) => setNuevo({ ...nuevo, tema: e.target.value })} aria-label="Tema del anuncio" />
          <input className="h-9 w-28 rounded-md border bg-background px-2 text-sm" placeholder="Gasto $" inputMode="numeric" value={nuevo.gasto} onChange={(e) => setNuevo({ ...nuevo, gasto: e.target.value })} aria-label="Gasto" />
          <Button size="sm" variant="outline" onClick={ligar} disabled={!nuevo.codigo.trim()}><Plus className="size-4" /> Ligar anuncio</Button>
        </div>
      </Seccion>
    </div>
  );
};

const EFECTOS = [
  { id: "descarta", name: "Descarta" },
  { id: "marca", name: "Marca para revisar" },
  { id: "cumple", name: "Solo cuenta como cumple" },
];
const ESTADOS = (["borrador", "activa", "pausada", "cerrada"] as EstadoVacante[]).map((e) => ({ id: e, name: ETQ_ESTADO[e] }));

const CamposVacante = () => {
  const { data: clientes } = useClientes();
  return (
    <>
      <SelectInput source="cliente_id" label="Cliente" choices={clientes.map((c) => ({ id: c.id, name: c.nombre }))} isRequired />
      <TextInput source="puesto" label="Puesto" isRequired />
      <TextInput source="zona" label="Zona" />
      <TextInput source="horario" label="Horario" />
      <TextInput source="sueldo" label="Sueldo" />
      <BooleanInput source="sueldo_autorizado" label="El cliente autoriza mostrar el sueldo" />
      <SelectInput source="estado" label="Estado" choices={ESTADOS} isRequired />
      <TextInput source="firmada_en" label="Firmada el (AAAA-MM-DD)" />
      <ArrayInput source="criterios" label="Criterios (cada uno: qué cuenta como cumple, y si su falta descarta o solo marca)">
        <SimpleFormIterator>
          <TextInput source="texto" label="Cuenta como cumple" isRequired />
          <SelectInput source="efecto" label="Si no cumple" choices={EFECTOS} isRequired />
          <TextInput source="texto_no" label="Cómo se lee cuando no cumple" />
          <TextInput source="pregunta" label="Pregunta que hace el bot" />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const transformarVacante = (d: any) => ({
  ...d,
  criterios: (d.criterios ?? []).map((c: CriterioVacante, i: number) => ({ ...c, clave: c.clave || `c${i + 1}-${Date.now().toString(36)}` })),
  // en Supabase `activa` es columna generada (estado = 'activa'); en demo hay que llevarla a mano
  activa: d.estado === "activa",
  firmada_en: d.firmada_en || null,
});

export const VacanteAlta = () => (
  <Create transform={transformarVacante} redirect="show" title="Alta de vacante">
    <SimpleForm defaultValues={{ estado: "borrador", criterios: [], sueldo_autorizado: false }}>
      <CamposVacante />
    </SimpleForm>
  </Create>
);
export const VacanteEditar = () => (
  <Edit transform={transformarVacante} redirect="show" title="Editar vacante" mutationMode="pessimistic">
    <SimpleForm>
      <CamposVacante />
    </SimpleForm>
  </Edit>
);

export const ChipEstadoVacante = ({ estado }: { estado: EstadoVacante }) => (
  <span className={cn("text-xs")}><Pildora tono={tonoEstado(estado)}>{ETQ_ESTADO[estado]}</Pildora></span>
);

export default { list: VacantesLista, show: VacanteFicha, create: VacanteAlta, edit: VacanteEditar, recordRepresentation: "puesto" };
